import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { type JSX, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";
import { CpsSpinner } from "corpus";

import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { FormControl, FormField, FormItem, FormRoot } from "@/components/ui/form/form";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { RichTextEditor } from "@/components/ui/rich-text/rich-text-editor";
import { useGraphQL, useGraphQLMutationWithErrorHandler } from "@/hooks/use-graphql.ts";

const AboutProfessionalSitePageQuery = graphql(/* GraphQL */ `
  query AboutProfessionalSitePageQuery {
    usuariosProfissionais {
      miniCurriculo
    }
  }
`);

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const AboutProfessionalSitePageMutation = graphql(`
  mutation AboutProfessionalSitePageMutation($miniCurriculo: String) {
    alteraConfiguracoesDoSite(input: { miniCurriculo: $miniCurriculo }) {
      sucesso
    }
  }
`);

const AboutProfessionalSiteSchema = z.object({
  miniCurriculo: z.string(),
});

const TWO_SECONDS = 2000;

const AboutProfessionalSitePage = (): JSX.Element => {
  const queryResult = useGraphQL(
    AboutProfessionalSitePageQuery,
    {},
    { staleTime: 0, gcTime: 0, refetchOnMount: "always" },
  );
  const { data } = queryResult;

  const { mutateAsync, isPending } = useGraphQLMutationWithErrorHandler(
    AboutProfessionalSitePageMutation,
  );

  const [showSaving, setShowSaving] = useState(false);

  useEffect(() => {
    if (isPending) {
      setShowSaving(true);
      return undefined;
    }

    if (showSaving) {
      const timer = setTimeout(() => {
        setShowSaving(false);
      }, TWO_SECONDS);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isPending, showSaving]);

  const form = useForm<z.infer<typeof AboutProfessionalSiteSchema>>({
    resolver: zodResolver(AboutProfessionalSiteSchema),
    values: {
      miniCurriculo: data?.usuariosProfissionais[0]?.miniCurriculo ?? "",
    },
  });

  const onSubmit = (response: z.infer<typeof AboutProfessionalSiteSchema>): void => {
    void mutateAsync({ miniCurriculo: response.miniCurriculo });
  };

  const saveRef = useRef<number | null>(null);

  const debouncedSave = (html: string) => {
    if (saveRef.current) window.clearTimeout(saveRef.current);
    saveRef.current = window.setTimeout(() => {
      onSubmit({ miniCurriculo: html });
    }, 700);
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Sobre você" />
      </HeaderRoot>
      <AsyncDataWrapper {...queryResult}>
        <FormRoot {...form}>
          {data?.usuariosProfissionais ? (
            <div className="flex flex-col h-full w-full">
              <FormField
                control={form.control}
                name="miniCurriculo"
                render={({ field }) => (
                  <FormItem className="h-full">
                    <FormControl className="h-full">
                      <div>
                        {showSaving ? (
                          <div className="z-100 text-xs px-2 py-1 bg-neutral-100 rounded-lg flex flex-row gap-1 w-auto absolute right-0 m-1">
                            <CpsSpinner className="h-3 w-3" />
                            Salvando...
                          </div>
                        ) : null}
                        <RichTextEditor
                          placeholder="Compartilhe sua trajetória profissional e especialidades. Apresente-se para seus futuros pacientes e destaque seus diferenciais!"
                          {...field}
                          onChange={(html: string) => {
                            field.onChange(html);
                            debouncedSave(html);
                          }}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          ) : null}
        </FormRoot>
      </AsyncDataWrapper>
    </>
  );
};

export const Route = createFileRoute("/settings/professional-site/about/")({
  component: AboutProfessionalSitePage,
});
